import { useEffect, useMemo, useState } from "react";
import { Form, Input, Table } from "antd";
import { Locale } from "../../utils";
import {
  buildMatrixForDisplay,
  findPropertyInGroup,
  syncSkuFormFields,
  getSkuPriceBoundsFromValues,
  formatSkuPriceRangeYuan,
  resolveGroupsForProductSpecs,
  skuListSignature,
} from "./productSpecHelpers";

const i18n = Locale.getInstance();

function BulkFillTitle({ title, placeholder, onApply }) {
  const [value, setValue] = useState("");

  const apply = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onApply(trimmed);
    setValue("");
  };

  return (
    <div className="sku-bulk-column">
      <span className="sku-bulk-column__title">{title}</span>
      <Input
        size="small"
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={apply}
      />
    </div>
  );
}

function buildColumns(groups, applyBulk, fieldKey) {
  const bulkPlaceholder = i18n.t("property.bulkFill");

  const groupColumns =
    groups?.map((group, key) => ({
      width: 100,
      title: i18n.getPropNameByLocale(group, "name"),
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            {record.labels?.[key] ??
              i18n.getPropNameByLocale(record, `name-${key}`)}
          </span>
          {key === 0 && record.image && (
            <img width={38} src={record.image} alt="" />
          )}
        </div>
      ),
    })) || [];

  return [
    ...groupColumns,
    {
      title: (
        <BulkFillTitle
          title={i18n.t("property.priceYuan")}
          placeholder={bulkPlaceholder}
          onApply={(value) => applyBulk("property_price", value)}
        />
      ),
      dataIndex: "price",
      key: "price",
      render: (_, item) => (
        <>
          <Form.Item
            style={{ margin: 0, display: "none" }}
            name={["property_id", item.code]}
            hidden
            initialValue={item.code}
          >
            <Input hidden />
          </Form.Item>
          <Form.Item
            key={`price-${item.code}-${fieldKey}`}
            style={{ margin: 0 }}
            name={["property_price", item.code]}
            initialValue={item.price}
            rules={[
              {
                required: true,
                message: i18n.t("property.priceWarning"),
              },
            ]}
          >
            <Input placeholder={i18n.t("property.priceYuanPlaceholder")} />
          </Form.Item>
        </>
      ),
    },
    {
      title: (
        <BulkFillTitle
          title={i18n.t("property.quantity")}
          placeholder={bulkPlaceholder}
          onApply={(value) => applyBulk("property_quantity", value)}
        />
      ),
      dataIndex: "quantity",
      key: "quantity",
      render: (_, item) => (
        <Form.Item
          key={`qty-${item.code}-${fieldKey}`}
          style={{ margin: 0 }}
          name={["property_quantity", item.code]}
          initialValue={item.quantity}
          rules={[
            {
              required: true,
              message: i18n.t("property.quantityWarning"),
            },
          ]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      title: (
        <BulkFillTitle
          title={i18n.t("property.number")}
          placeholder={bulkPlaceholder}
          onApply={(value) => applyBulk("property_product_no", value)}
        />
      ),
      dataIndex: "product_no",
      key: "product_no",
      render: (_, item) => (
        <Form.Item
          key={`no-${item.code}-${fieldKey}`}
          style={{ margin: 0 }}
          name={["property_product_no", item.code]}
          initialValue={item.product_no}
        >
          <Input />
        </Form.Item>
      ),
    },
  ];
}

const PropertyTable = ({ groups, skuList, parentForm }) => {
  const form = Form.useFormInstance();
  const isZh = !i18n.isLocaleRTL();
  const [fieldKey, setFieldKey] = useState(0);

  const effectiveGroups =
    groups?.length > 0
      ? groups
      : resolveGroupsForProductSpecs([], skuList, isZh);

  const matrixRows = useMemo(
    () => buildMatrixForDisplay(effectiveGroups, skuList, isZh),
    [effectiveGroups, skuList, isZh]
  );

  const dataSource = useMemo(() => {
    return matrixRows.map((row) => {
      const item = {
        code: row.code,
        price: row.price,
        quantity: row.quantity,
        product_no: row.product_no,
        labels: row.labels,
      };

      if (effectiveGroups.length === 1) {
        const prop =
          findPropertyInGroup(effectiveGroups[0], row.code) ||
          effectiveGroups[0].childList?.find(
            (p) =>
              (p.id != null && String(p.id) === row.code) ||
              p.zh_name?.trim() === row.code ||
              p.ug_name?.trim() === row.code
          );
        if (prop) {
          item["zh_name-0"] = prop.zh_name;
          item["ug_name-0"] = prop.ug_name;
          item.image = prop.image;
        }
      } else if (effectiveGroups.length >= 2) {
        const ids = row.code.split("-");
        const labelGroups =
          effectiveGroups.length === ids.length
            ? effectiveGroups
            : row.labels?.length
              ? row.labels.map((label) => ({
                  childList: [{ zh_name: label, ug_name: label }],
                }))
              : effectiveGroups;
        labelGroups.forEach((group, idx) => {
          const prop = findPropertyInGroup(group, ids[idx]);
          if (prop) {
            item[`zh_name-${idx}`] = prop.zh_name;
            item[`ug_name-${idx}`] = prop.ug_name;
            if (idx === 0) item.image = prop.image;
          } else if (row.labels?.[idx]) {
            item[`zh_name-${idx}`] = row.labels[idx];
            item[`ug_name-${idx}`] = row.labels[idx];
          }
        });
      }

      return item;
    });
  }, [matrixRows, effectiveGroups]);

  const codes = dataSource.map((row) => row.code);
  const codeKey = codes.join("|");
  const skuSignature = skuListSignature(skuList);

  useEffect(() => {
    const formRef = form ? { current: form } : parentForm;
    if (!formRef?.current) return;

    if (codes.length === 0) {
      if (!skuList?.length) {
        formRef.current.setFieldsValue({
          property_id: {},
          property_price: {},
          property_quantity: {},
          property_product_no: {},
        });
      }
      return;
    }

    syncSkuFormFields(formRef, effectiveGroups, skuList, isZh);
    setFieldKey((key) => key + 1);
  }, [codeKey, skuSignature, effectiveGroups?.length, form, isZh]);

  if (dataSource.length === 0) return null;

  const applyBulk = (fieldName, value) => {
    const activeForm = form || parentForm?.current;
    if (!activeForm) return;

    const next = {};
    codes.forEach((code) => {
      next[code] = value;
    });
    activeForm.setFieldsValue({ [fieldName]: next });
  };

  return (
    <>
      <Form.Item noStyle shouldUpdate>
        {() => {
          const activeForm = form || parentForm?.current;
          const values = activeForm?.getFieldsValue(true) || {};
          const bounds = getSkuPriceBoundsFromValues(
            effectiveGroups,
            skuList,
            values,
            isZh
          );
          const rangeText = formatSkuPriceRangeYuan(bounds);
          if (!rangeText) return null;

          return (
            <div className="sku-price-range">
              {i18n.t("productEdit.multiSpecPriceRange")}: {rangeText}
            </div>
          );
        }}
      </Form.Item>
      <Table
        className="sku-property-table"
        style={{ margin: "20px" }}
        columns={buildColumns(effectiveGroups, applyBulk, fieldKey)}
        scroll={{ x: 1000, y: 400 }}
        rowKey="code"
        dataSource={dataSource}
        pagination={false}
        bordered
      />
    </>
  );
};

export default PropertyTable;
